import { useCallback, useEffect, useRef } from 'react'

/**
 * Ref-backed `setTimeout` with automatic cleanup: `set` clears any previous pending timer before
 * scheduling a new one, and the timer is also cleared on unmount so its callback can never fire
 * against an unmounted component.
 *
 * @returns
 *  - `set`   – schedules `callback` after `delayMs`, replacing any pending timer
 *  - `clear` – cancels the pending timer, if any
 */
function useTimeout(): { set: (callback: () => void, delayMs: number) => void, clear: () => void } {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const clear = useCallback(() => {
    clearTimeout(timeoutRef.current)
  }, [])

  const set = useCallback((callback: () => void, delayMs: number) => {
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(callback, delayMs)
  }, [])

  useEffect(() => clear, [clear])

  return { set, clear }
}

export default useTimeout
